import type { IconList } from '@snowit/assets';
import type PERMISSIONS from '@snowit/utilities/permissions';

import {
  AvailabilityPath,
  CategoriesPath,
  CouponPath,
  DiscountsPath,
  ExternalCodesPath,
  OptionsPath,
  SeasonsPath,
  StagesPath,
  TagsPath,
} from './configs/catalog';
import { ChangelogsPath } from './configs/changelogs';
import { GraphPath, LookerStudioPath, MonitoringPath, TaskAIPath } from './configs/charts';
import { DealersPath, ProvidersPath } from './configs/dealers_and_providers';
import { ChallengesPath, HomePath, MyPartnerPath, TurnstilesPath } from './configs/home';
import {
  MembershipsPath,
  MembershipsStatsPath,
  SogliePPUPath,
  TariffarioPPUPath,
  TransitsPath,
} from './configs/memberships';
import {
  CarouselsPath,
  DeparturesPath,
  DistrictsPath,
  FileDrivePath,
  GalleriesPath,
  NotificationsPath,
  PartnersPath,
  PoliciesPath,
  ReviewsPath,
  ShippingPath,
  SnowitcardsPath,
  StageRoutesPath,
} from './configs/misc';
import { CartsPath, CassaPath, OrdersPath } from './configs/orders';
import { PricesPath } from './configs/prices';
import { ProductPath } from './configs/products';
import { CheckoutCodesPath, HotelPromoPath, SkipassGroupsPath } from './configs/promos';
import { QuickSearchCardPath, QuickSearchMembershipPath, QuickSearchPath } from './configs/search';
import {
  ApiKeysPath,
  EmailPath,
  ImportExportPath,
  PDFPath,
  QuickbookPath,
  SettingsPath,
  WebshopPath,
} from './configs/settings';
import { AdminToolsPath } from './configs/tools';
import {
  BrevoEmailsPath,
  PermissionsPath,
  ProfilesPath,
  RolesPath,
  UserCreatePath,
  UsersPath,
} from './configs/users';
import { VenuesPath } from './configs/venues';
import { ZonesPath } from './configs/zones';

export const ALL_MAIN_ROUTES = [
  '/',
  'admin_tools',
  'apikeys',
  'availability',
  'carousels',
  'cart',
  'cassaskipass',
  'categories',
  'challenges',
  'changelogs',
  'check_soglie_ppu',
  'checkout_codes',
  'coupons_hotel',
  'coupons',
  'dealers',
  'departures',
  'discounts',
  'districts',
  'emails',
  'external_codes',
  'file_drive',
  'galleries',
  'graphs',
  'import_export',
  'looker',
  'memberships_stats',
  'memberships',
  'monitoring',
  'my_partner',
  'notifications',
  'orders',
  'partners',
  'pdf',
  'permissions',
  'policies',
  'prices',
  'product_options',
  'products',
  'profiles',
  'providers',
  'quick_search_card',
  'quick_search_membership',
  'quick_search',
  'quickbooks',
  'reviews',
  'roles',
  'routes',
  'seasons',
  'settings',
  'shipping_methods',
  'skipass_groups',
  'snowitcards',
  'stages',
  'tags',
  'tariffario_ppu',
  'task_ai',
  'transits',
  'turnstiles',
  'user_create',
  'user_emails',
  'users',
  'venues',
  'webshop',
  'zones',
] as const;

export const ALL_GROUPS = [
  'home',
  'charts',
  'search',
  'users',
  'orders',
  'cards',
  'catalog',
  'providers',
  'dealers',
  'promos',
  'misc',
  'settings',
  'admin_tools',
  'changelogs',
  'lep',
] as const;

export type RouteGroup = (typeof ALL_GROUPS)[number];
export type MainRoute = (typeof ALL_MAIN_ROUTES)[number];
export type MainPath = `/${MainRoute}` | `/${MainRoute}/${string}`;

export const allPaths: Record<MainRoute, MainPath> = {
  '/': HomePath,
  admin_tools: AdminToolsPath,
  apikeys: ApiKeysPath,
  availability: AvailabilityPath,
  carousels: CarouselsPath,
  cart: CartsPath,
  cassaskipass: CassaPath,
  categories: CategoriesPath,
  challenges: ChallengesPath,
  changelogs: ChangelogsPath,
  checkout_codes: CheckoutCodesPath,
  coupons_hotel: HotelPromoPath,
  coupons: CouponPath,
  dealers: DealersPath,
  departures: DeparturesPath,
  discounts: DiscountsPath,
  districts: DistrictsPath,
  emails: EmailPath,
  external_codes: ExternalCodesPath,
  file_drive: FileDrivePath,
  galleries: GalleriesPath,
  graphs: GraphPath,
  import_export: ImportExportPath,
  looker: LookerStudioPath,
  memberships_stats: MembershipsStatsPath,
  memberships: MembershipsPath,
  monitoring: MonitoringPath,
  my_partner: MyPartnerPath,
  notifications: NotificationsPath,
  orders: OrdersPath,
  partners: PartnersPath,
  pdf: PDFPath,
  permissions: PermissionsPath,
  policies: PoliciesPath,
  prices: PricesPath,
  product_options: OptionsPath,
  products: ProductPath,
  profiles: ProfilesPath,
  providers: ProvidersPath,
  quick_search_card: QuickSearchCardPath,
  quick_search_membership: QuickSearchMembershipPath,
  quick_search: QuickSearchPath,
  quickbooks: QuickbookPath,
  reviews: ReviewsPath,
  roles: RolesPath,
  routes: StageRoutesPath,
  seasons: SeasonsPath,
  settings: SettingsPath,
  shipping_methods: ShippingPath,
  skipass_groups: SkipassGroupsPath,
  snowitcards: SnowitcardsPath,
  stages: StagesPath,
  tags: TagsPath,
  tariffario_ppu: TariffarioPPUPath,
  task_ai: TaskAIPath,
  check_soglie_ppu: SogliePPUPath,
  transits: TransitsPath,
  turnstiles: TurnstilesPath,
  user_emails: BrevoEmailsPath,
  user_create: UserCreatePath,
  users: UsersPath,
  venues: VenuesPath,
  webshop: WebshopPath,
  zones: ZonesPath,
};

export type NavOptions = {
  path: [MainRoute, ...(string | number | null | undefined)[]];
  querystring?: Record<string, any>;
  replace?: boolean;
  absolute?: boolean;
  target?: '_blank' | '_self';
};

export type NavGroup = {
  id: RouteGroup;
  label?: string;
  hidden?: boolean;
  routes: MainNavRoute[];
};

export type NavRouteType = 0 | 1 | 2;

export type NavRoute<T extends string = string> = {
  path: T;
  label?: string;
  icon?: IconList;
  children?: NavRoute[];
  /** 0 = Admin, 1 = Partner, 2 = User. default is 0 (Admin) */
  type?: NavRouteType;
  /** disowned children do not inherit permissions from parent */
  disowned?: boolean;
  kb?: string;
  permissions?: PERMISSIONS[];
  /**
   * permissions logic, by default all permissions must be satisfied (AND),
   * (OR) allows for at least one to be satisfied
   * WARNING!!!: Permissions are shared with children (unless disowned)
   * but either the child has a OR logic itself or it will have a AND logic, you cannot mix and match
   * (e.g.) if parents has permission OR(A, B) and child has permission C
   * if child has OR logic, either A or B or C will satisfy the permission (thus permission C is redundant)
   * if child has AND logic, A,B and C must be satisfied (so it was pointless to have OR permissions on the parent)
   * you CANNOT have child be satisfied by C AND (A OR B)
   *
   * logic is not propagated to children.
   */
  logic?: 'AND' | 'OR';
  hidden?: boolean;
};

export type MainNavRoute = NavRoute<`/${MainRoute}`>;
